/**
 * Recruit — browse the usefoundry athlete dataset (agent epic PR 3).
 *
 * THE agent-plan paid unlock (amended D7). Gating happens twice, on
 * purpose: `useFeature('recruitingDataset')` decides what to RENDER (free
 * agents get an upsell card, not an error), and the server's
 * `requireFeature` middleware on `agency.searchDataset` remains the
 * authoritative gate. The page deliberately has no SSR loader prefetch —
 * a gated SSR query would error the whole route for free users, and
 * `isFeatureGateError` still catches the race where the optimistic
 * client-side allow guesses wrong.
 *
 * Filter dropdowns are static option lists mirroring the real dataset's
 * slugs (sport, division, recruiting class year) rather than
 * provider-supplied facets — keeps the provider contract minimal. Filtering itself runs
 * inside the provider, never post-query JS here (the brand-directory
 * landmine).
 */

import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@app/ui';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../../../components/route-error';
import { api } from '../../../../lib/api';
import { isFeatureGateError } from '../../../../lib/feature-gate-client';
import { divisionLabel, formatMoneyCompact, formatReach, sportLabel } from '../../../../lib/format-reach';
import { useFeature } from '../../../../lib/use-feature';

// Radix Select disallows empty-string values — sentinel for "all".
const ALL = '__all__';

// Fallback filter dimensions, shown only until `agency.datasetFacets`
// returns the REAL value space of the connected dataset (every sport,
// class year, and division that actually has athletes).
const FALLBACK_SPORTS = [
  'football',
  'basketball_mens',
  'basketball_womens',
  'baseball',
  'softball',
  'volleyball',
  'soccer',
  'track_field',
  'gymnastics',
  'swimming',
];
const FALLBACK_DIVISIONS = ['ncaa-d1', 'ncaa-d2', 'ncaa-d3', 'naia', 'juco'];
const FALLBACK_CLASS_YEARS = ['2025', '2026', '2027', '2028'];
const REACH_BUCKETS = [
  { value: '10000', label: '10k+ reach' },
  { value: '50000', label: '50k+ reach' },
  { value: '100000', label: '100k+ reach' },
  { value: '250000', label: '250k+ reach' },
  { value: '500000', label: '500k+ reach' },
];
const VALUATION_BUCKETS = [
  { value: '50000', label: '$50k+ NIL value' },
  { value: '100000', label: '$100k+ NIL value' },
  { value: '500000', label: '$500k+ NIL value' },
  { value: '1000000', label: '$1M+ NIL value' },
];

export const Route = createFileRoute('/_authenticated/agency/recruit/')({
  validateSearch: z.object({
    query: z.string().optional(),
    sport: z.string().optional(),
    division: z.string().optional(),
    classYear: z.string().optional(),
    followersMin: z.coerce.number().optional(),
    valuationMin: z.coerce.number().optional(),
    page: z.coerce.number().default(1),
  }),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: RecruitPage,
});

function RecruitPage() {
  const gate = useFeature('recruitingDataset');
  if (!gate.loading && !gate.allowed) {
    return <DatasetUpsell />;
  }
  return <RecruitBrowse />;
}

function DatasetUpsell() {
  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-2">Recruit</h1>
      <Card>
        <CardContent className="py-10 text-center space-y-3">
          <p className="font-medium">The recruiting dataset is an Agent plan feature.</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Browse thousands of enriched athlete profiles — sport, school, class year, and
            per-platform reach — filter to your niche, and import prospects straight onto your
            pipeline. Your roster and invites stay free either way.
          </p>
          <Button asChild>
            <Link to="/pricing" search={{ limit: 'recruitingDataset' }}>
              See Agent plans
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function RecruitBrowse() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  const { data: mine } = useSuspenseQuery(api.agency.myAgencies.queryOptions());
  // Layout guard guarantees at least one agency (same convention as roster).
  const orgId = mine.agencies[0].id;

  const [queryInput, setQueryInput] = useState(search.query || '');
  const currentQuery = search.query || '';
  useEffect(() => {
    const timer = setTimeout(() => {
      if (queryInput !== currentQuery) {
        navigate({ search: (prev) => ({ ...prev, query: queryInput || undefined, page: 1 }) });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [queryInput, currentQuery, navigate]);

  const searchInput = {
    query: search.query,
    sport: search.sport,
    division: search.division,
    classYear: search.classYear,
    followersMin: search.followersMin,
    valuationMin: search.valuationMin,
    page: search.page,
    limit: 20,
  };
  const { data, error, isLoading, isPlaceholderData } = useQuery({
    ...api.agency.searchDataset.queryOptions({ input: searchInput }),
    // Keep the previous results on screen while a filter change loads —
    // no skeleton flash — and cache pages for 10 minutes (the dataset
    // rebuilds every 6h; within a session, revisits should be instant).
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
  });

  // The real value space of the filters, straight from the dataset.
  // Cached hard — it only changes when the dataset rebuilds.
  const { data: facets } = useQuery({
    ...api.agency.datasetFacets.queryOptions(),
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
  });
  const sports = facets?.sports?.length ? facets.sports : FALLBACK_SPORTS;
  const divisions = facets?.divisions?.length ? facets.divisions : FALLBACK_DIVISIONS;
  const classYears = facets?.classYears?.length ? facets.classYears : FALLBACK_CLASS_YEARS;

  // Make the Next click instant: warm the following page in the background
  // as soon as the current one lands.
  const totalPagesSoFar = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  useEffect(() => {
    if (!data || isPlaceholderData || search.page >= totalPagesSoFar) return;
    queryClient.prefetchQuery({
      ...api.agency.searchDataset.queryOptions({
        input: { ...searchInput, page: search.page + 1 },
      }),
      staleTime: 10 * 60 * 1000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isPlaceholderData, search.page]);

  // Which dataset athletes are already on the pipeline → per-row state.
  const { data: prospects } = useQuery(
    api.agency.listProspects.queryOptions({ input: { orgId } }),
  );
  const importedIds = new Set(
    (prospects ?? []).map((p) => p.foundryAthleteId).filter(Boolean) as string[],
  );

  const importMutation = useMutation({
    ...api.agency.importProspect.mutationOptions(),
    onSuccess: () => {
      toast.success('Imported to your pipeline — invite them from the Roster page.');
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'agency',
      });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Import failed'),
  });

  if (error && isFeatureGateError(error)) return <DatasetUpsell />;
  if (error) throw error;

  const setFilter = (key: string, value: string | number | undefined) =>
    navigate({ search: (prev) => ({ ...prev, [key]: value, page: 1 }) });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Recruit</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data ? (
            <>
              {data.total} athlete{data.total === 1 ? '' : 's'} in the dataset
              {data.provider === 'mock' && (
                <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs">
                  sample data — live dataset connects via API key
                </span>
              )}
            </>
          ) : (
            'Loading dataset…'
          )}
        </p>
      </div>

      <div className="mb-6 space-y-3">
        <Input
          placeholder="Search by name or school…"
          className="w-full sm:max-w-md"
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
        />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <FilterSelect
            value={search.sport ?? ALL}
            onChange={(v) => setFilter('sport', v === ALL ? undefined : v)}
            options={[
              { value: ALL, label: 'All sports' },
              ...sports.map((s) => ({ value: s, label: sportLabel(s) })),
            ]}
          />
          <FilterSelect
            value={search.division ?? ALL}
            onChange={(v) => setFilter('division', v === ALL ? undefined : v)}
            options={[
              { value: ALL, label: 'All divisions' },
              ...divisions.map((d) => ({ value: d, label: divisionLabel(d) })),
            ]}
          />
          <FilterSelect
            value={search.classYear ?? ALL}
            onChange={(v) => setFilter('classYear', v === ALL ? undefined : v)}
            options={[
              { value: ALL, label: 'All class years' },
              ...classYears.map((c) => ({ value: c, label: `Class of ${c}` })),
            ]}
          />
          <FilterSelect
            value={search.followersMin ? String(search.followersMin) : ALL}
            onChange={(v) => setFilter('followersMin', v === ALL ? undefined : Number(v))}
            options={[{ value: ALL, label: 'Any reach' }, ...REACH_BUCKETS]}
          />
          <FilterSelect
            value={search.valuationMin ? String(search.valuationMin) : ALL}
            onChange={(v) => setFilter('valuationMin', v === ALL ? undefined : Number(v))}
            options={[{ value: ALL, label: 'Any NIL value' }, ...VALUATION_BUCKETS]}
          />
        </div>
      </div>

      {!data ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-14 bg-muted rounded" />
          ))}
        </div>
      ) : data.athletes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No athletes match those filters — try widening them.
          </CardContent>
        </Card>
      ) : (
        <Card className={isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Athlete</th>
                  <th className="px-3 py-2 text-left font-medium">Class</th>
                  <th className="px-3 py-2 text-left font-medium">Division</th>
                  <th className="px-3 py-2 text-right font-medium">NIL value</th>
                  <th className="px-3 py-2 text-right font-medium">Reach</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.athletes.map((a) => {
                  const imported = importedIds.has(a.foundryId);
                  return (
                    <tr key={a.foundryId} className="border-t">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          {a.photoUrl && (
                            <img
                              src={a.photoUrl}
                              alt=""
                              className="h-8 w-8 rounded-full object-cover bg-muted shrink-0"
                              loading="lazy"
                            />
                          )}
                          <div>
                            <Link
                              to="/agency/recruit/$foundryId"
                              params={{ foundryId: a.foundryId }}
                              className="font-medium text-primary hover:underline"
                            >
                              {a.name}
                            </Link>
                            <div className="text-xs text-muted-foreground">
                              {[sportLabel(a.sport), a.position, a.school]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                            {a.rankingConsensus && (
                              <div className="text-xs text-amber-700">{a.rankingConsensus}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">{a.classYear ?? '—'}</td>
                      <td className="px-3 py-3">{divisionLabel(a.division)}</td>
                      <td className="px-3 py-3 text-right">{formatMoneyCompact(a.nilValuation)}</td>
                      <td className="px-3 py-3 text-right">{formatReach(a.socialFollowers)}</td>
                      <td className="px-3 py-3 text-right">
                        {imported ? (
                          <span className="text-xs text-muted-foreground">On pipeline ✓</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={importMutation.isPending}
                            onClick={() =>
                              importMutation.mutate({ orgId, foundryId: a.foundryId })
                            }
                          >
                            Import
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {data && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={search.page <= 1}
            onClick={() => navigate({ search: (prev) => ({ ...prev, page: search.page - 1 }) })}
          >
            Previous
          </Button>
          <span className="text-muted-foreground">
            Page {search.page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={search.page >= totalPages}
            onClick={() => navigate({ search: (prev) => ({ ...prev, page: search.page + 1 }) })}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
